import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BookOpen, Wrench, Droplets, Truck, RotateCcw, ShieldCheck, Download,
  MessageCircle, Check, ArrowRight, Eye, Wine,
} from "lucide-react";
import { BRAND } from "@/lib/api";
import { DocPreviewModal } from "@/components/DocPreviewModal";

const MANUAL_URL = BRAND.manualUrl;

const openMat = () => window.dispatchEvent(new Event("preshh:open-mat"));

const CARDS = [
  { icon: BookOpen, key: "manual", href: MANUAL_URL, ext: true },
  { icon: Wrench, key: "setup", href: MANUAL_URL, ext: true },
  { icon: Droplets, key: "cleaning", href: MANUAL_URL, ext: true },
  { icon: Truck, key: "shipping", href: "/legal/shipping", ext: false },
  { icon: RotateCcw, key: "returns", href: "/legal/returns", ext: false },
  { icon: ShieldCheck, key: "warranty", href: "/legal/terms", ext: false },
];

export default function Support() {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const assistPoints = t("support.assist_points", { returnObjects: true }) || [];

  return (
    <div className="text-white" data-testid="support-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #00B5D8 0, transparent 45%), radial-gradient(circle at 85% 15%, #00B5D8 0, transparent 40%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center">
          <div>
            <p className="preshh-eyebrow text-[#00B5D8]" data-testid="support-eyebrow">{t("support.eyebrow")}</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" data-testid="support-title">
              {t("support.title")}
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/65 leading-relaxed" data-testid="support-lead">
              {t("support.lead")}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button onClick={openMat} data-testid="support-chat-cta"
                className="preshh-btn-primary text-sm inline-flex items-center gap-2">
                <MessageCircle size={18} /> {t("support.cta_chat")}
              </button>
              <a href="#support-topics" data-testid="support-browse-cta"
                className="preshh-btn-ghost text-sm inline-flex items-center gap-2">
                {t("support.cta_browse")} <ArrowRight size={16} />
              </a>
            </div>
          </div>

          {/* Mat assist card */}
          <div className="relative">
            <div className="rounded-3xl border border-[#00B5D8]/25 bg-gradient-to-b from-[#0c1416] to-[#0a0a0a] p-7 md:p-8 shadow-2xl" data-testid="support-mat-card">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <img src={BRAND.matAvatar} alt="Mat" className="w-14 h-14 rounded-full object-cover ring-2 ring-[#00B5D8]/40" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#22c55e] ring-2 ring-[#0a0a0a]" />
                </div>
                <div>
                  <p className="font-display text-lg leading-none">{t("concierge.name")}</p>
                  <p className="mt-1 text-xs text-white/50">{t("concierge.role")}</p>
                </div>
              </div>
              <p className="mt-6 text-sm font-semibold text-white/90">{t("support.assist_title")}</p>
              <ul className="mt-4 space-y-3">
                {assistPoints.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#00B5D8]/15 flex items-center justify-center">
                      <Check size={12} className="text-[#00B5D8]" strokeWidth={3} />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
              <button onClick={openMat} data-testid="support-mat-card-cta"
                className="mt-7 w-full preshh-btn-primary text-sm inline-flex items-center justify-center gap-2">
                <MessageCircle size={16} /> {t("support.cta_chat")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        {/* Resources: Manual + Recipes with elegant preview */}
        <div className="mb-14 grid md:grid-cols-2 gap-4">
          {[
            { key: "manual", icon: BookOpen, url: BRAND.manualUrl, title: t("support.manual_title"), body: t("support.manual_body") },
            { key: "recipes", icon: Wine, url: BRAND.recipesUrl, title: t("support.recipes_title"), body: t("support.recipes_body") },
          ].map((r) => (
            <div key={r.key} className="bg-[#111] border border-[#00B5D8]/30 rounded-3xl p-6 md:p-7 flex flex-col gap-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#00B5D8]/12 flex items-center justify-center flex-shrink-0">
                  <r.icon size={22} className="text-[#00B5D8]" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold">{r.title}</p>
                  <p className="text-sm text-white/60">{r.body}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 mt-auto">
                <button onClick={() => setDoc(r)} data-testid={`${r.key}-preview`}
                  className="preshh-btn-primary text-sm inline-flex items-center gap-2">
                  <Eye size={16} /> {t("support.preview_cta")}
                </button>
                <a href={r.url} download data-testid={r.key === "manual" ? "manual-download" : "recipes-download"}
                  className="preshh-btn-ghost text-sm inline-flex items-center gap-2">
                  <Download size={16} /> {t("support.manual_cta")}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Topics */}
        <h2 id="support-topics" className="font-display text-2xl md:text-3xl mb-6 scroll-mt-28">{t("support.cards_title")}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {CARDS.map(({ icon: Icon, key, href, ext }) => {
            const inner = (
              <>
                <div className="w-11 h-11 rounded-2xl bg-white/5 group-hover:bg-[#00B5D8]/12 flex items-center justify-center transition-colors">
                  <Icon size={20} strokeWidth={1.6} className="text-[#00B5D8]" />
                </div>
                <h3 className="mt-4 font-bold">{t(`support.card.${key}.t`)}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{t(`support.card.${key}.d`)}</p>
              </>
            );
            const cls = "group bg-[#111] border border-white/10 rounded-2xl p-6 hover:border-[#00B5D8]/40 hover:-translate-y-0.5 transition-all block";
            return ext ? (
              <a key={key} href={href} target="_blank" rel="noreferrer" data-testid={`support-card-${key}`} className={cls}>{inner}</a>
            ) : (
              <Link key={key} to={href} data-testid={`support-card-${key}`} className={cls}>{inner}</Link>
            );
          })}
        </div>

        {/* Help footer */}
        <div className="mt-16 bg-[#111] border border-white/10 rounded-3xl p-8 md:p-10 text-center">
          <p className="font-display text-2xl md:text-3xl">{t("support.help_title")}</p>
          <p className="mt-3 text-white/60 text-sm">
            {t("support.help_body")}{" "}
            <a href="mailto:support@preshh.com.br" className="text-[#00B5D8] hover:underline">support@preshh.com.br</a>
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button onClick={openMat} data-testid="support-footer-chat"
              className="preshh-btn-primary text-sm inline-flex items-center gap-2">
              <MessageCircle size={16} /> {t("support.cta_chat")}
            </button>
            <Link to="/contact" data-testid="support-contact-cta" className="preshh-btn-ghost text-sm">{t("support.help_cta")}</Link>
          </div>
        </div>
      </div>

      <DocPreviewModal
        open={!!doc}
        onOpenChange={(o) => !o && setDoc(null)}
        url={doc?.url}
        title={doc?.title}
        subtitle={doc?.body}
        downloadLabel={t("support.manual_cta")}
        openLabel={t("support.open_new_tab")}
      />
    </div>
  );
}
